var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useLayoutEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import Stack from '@mui/material/Stack';
import { useDocTitle } from '@/hooks/doc-title';
import { useUIPermissions } from '@/api/sessions/self/ui';
import { withAuthenticationRequired } from '@/hocs/authentication-required';
import { CONFIG_TEMPLATES_ROUTE, AUTH_TEMPLATES_ROUTE, SCRIPT_TEMPLATES_ROUTE, USER_GROUP_TEMPLATES_ROUTE, NETOPS_TEMPLATES_ROUTE, SMF_TEMPLATES_ROUTE, PORT_LOGGING_TEMPLATES_ROUTE, NOT_FOUND_ROUTE, } from '@/constants/routes';
import { ConfigTemplateTab } from '@/containers/config/templates/config-template-table';
import { pathEndsWith } from '@/utils/navigation';
import { NewConfigTemplateMenu } from '@/containers/menus/new-config-template-menu';
var TabValue;
(function (TabValue) {
    TabValue["AUTH"] = "AUTH";
    TabValue["SCRIPT"] = "SCRIPT";
    TabValue["USER"] = "USER";
    TabValue["NETOPS"] = "NOM";
    TabValue["SMF"] = "SMF";
    TabValue["PORT"] = "PORT";
})(TabValue || (TabValue = {}));
function ConfigTemplates() {
    var t = useTranslation().t;
    var pathname = useLocation().pathname;
    var navigate = useNavigate();
    var _a = useUIPermissions(), permissions = _a.permissions, hasPermission = _a.hasPermission;
    var templatePermissions = useMemo(function () { return ({
        hasAuthViewAccess: hasPermission('configure.configuration_templating.authentication_templates.view'),
        hasAuthEditAccess: hasPermission('configure.configuration_templating.authentication_templates.edit'),
        hasScriptViewAccess: hasPermission('configure.configuration_templating.script_templates.view'),
        hasScriptEditAccess: hasPermission('configure.configuration_templating.script_templates.edit'),
        hasUserGroupTemplateViewAccess: hasPermission('configure.configuration_templating.users_groups_templates.view'),
        hasUserGroupTemplateEditAccess: hasPermission('configure.configuration_templating.users_groups_templates.edit'),
        hasSmfTemplateViewAccess: hasPermission('configure.configuration_templating.smf_templates.view'),
        hasSmfTemplateEditAccess: hasPermission('configure.configuration_templating.smf_templates.edit'),
        hasTemplatePush: hasPermission('configure.configuration_templating.apply_templates.push'),
    }); }, [hasPermission]);
    useDocTitle(t('pages.config_templates.title'));
    var tabValue;
    switch (pathname) {
        case SCRIPT_TEMPLATES_ROUTE:
            tabValue = TabValue.SCRIPT;
            break;
        case USER_GROUP_TEMPLATES_ROUTE:
            tabValue = TabValue.USER;
            break;
        case NETOPS_TEMPLATES_ROUTE:
            tabValue = TabValue.NETOPS;
            break;
        case SMF_TEMPLATES_ROUTE:
            tabValue = TabValue.SMF;
            break;
        case PORT_LOGGING_TEMPLATES_ROUTE:
            tabValue = TabValue.PORT;
            break;
        default:
            tabValue = TabValue.AUTH;
            break;
    }
    useLayoutEffect(function () {
        if ((permissions === null || permissions === void 0 ? void 0 : permissions.is_secondary) || (permissions === null || permissions === void 0 ? void 0 : permissions.is_lic_expired)) {
            navigate(NOT_FOUND_ROUTE);
        }
        if (pathEndsWith(pathname, CONFIG_TEMPLATES_ROUTE)) {
            if (templatePermissions.hasAuthViewAccess) {
                if (pathEndsWith(pathname, NETOPS_TEMPLATES_ROUTE)) {
                    navigate(NETOPS_TEMPLATES_ROUTE);
                }
                else if (pathEndsWith(pathname, PORT_LOGGING_TEMPLATES_ROUTE)) {
                    navigate(PORT_LOGGING_TEMPLATES_ROUTE);
                }
                navigate(AUTH_TEMPLATES_ROUTE);
            }
            else if (templatePermissions.hasScriptViewAccess) {
                navigate(SCRIPT_TEMPLATES_ROUTE);
            }
            else if (templatePermissions.hasUserGroupTemplateViewAccess) {
                navigate(USER_GROUP_TEMPLATES_ROUTE);
            }
            else if (templatePermissions.hasSmfTemplateViewAccess) {
                navigate(SMF_TEMPLATES_ROUTE);
            }
            else
                navigate(NOT_FOUND_ROUTE);
        }
    }, [pathname, navigate, templatePermissions, permissions === null || permissions === void 0 ? void 0 : permissions.is_secondary, permissions === null || permissions === void 0 ? void 0 : permissions.is_lic_expired]);
    return (_jsxs(Box, __assign({ margin: 2 }, { children: [_jsxs(Stack, __assign({ direction: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }, { children: [_jsx(Typography, __assign({ component: "h2", variant: "h2" }, { children: t('pages.config_templates.title') })), _jsx(NewConfigTemplateMenu, { permissions: templatePermissions })] })), _jsx(Paper, { children: _jsxs(TabContext, __assign({ value: tabValue }, { children: [_jsxs(TabList, __assign({ orientation: "horizontal", scrollButtons: true, variant: "scrollable" }, { children: [_jsx(Tab, { component: RouterLink, to: AUTH_TEMPLATES_ROUTE, label: t('pages.config_templates.tabs.auth.title'), value: TabValue.AUTH, disabled: !templatePermissions.hasAuthViewAccess }), _jsx(Tab, { component: RouterLink, to: NETOPS_TEMPLATES_ROUTE, label: t('pages.config_templates.tabs.netops.title'), value: TabValue.NETOPS, disabled: !templatePermissions.hasAuthViewAccess }), _jsx(Tab, { component: RouterLink, to: PORT_LOGGING_TEMPLATES_ROUTE, label: t('pages.config_templates.tabs.port_logging.title'), value: TabValue.PORT, disabled: !templatePermissions.hasAuthViewAccess }), _jsx(Tab, { component: RouterLink, to: SCRIPT_TEMPLATES_ROUTE, label: t('pages.config_templates.tabs.script.title'), value: TabValue.SCRIPT, disabled: !templatePermissions.hasScriptViewAccess }), _jsx(Tab, { component: RouterLink, to: SMF_TEMPLATES_ROUTE, label: t('pages.config_templates.tabs.smf.title'), value: TabValue.SMF, disabled: !templatePermissions.hasSmfTemplateViewAccess }), _jsx(Tab, { component: RouterLink, to: USER_GROUP_TEMPLATES_ROUTE, label: t('pages.config_templates.tabs.users_groups.title'), value: TabValue.USER, disabled: !templatePermissions.hasUserGroupTemplateViewAccess })] })), _jsxs(_Fragment, { children: [_jsx(TabPanel, __assign({ value: TabValue.AUTH }, { children: _jsx(ConfigTemplateTab, { type: "auth", canPushTemplates: templatePermissions.hasTemplatePush }) })), _jsx(TabPanel, __assign({ value: TabValue.SCRIPT }, { children: _jsx(ConfigTemplateTab, { type: "script", canPushTemplates: templatePermissions.hasTemplatePush }) })), _jsx(TabPanel, __assign({ value: TabValue.USER }, { children: _jsx(ConfigTemplateTab, { type: "users_groups", canPushTemplates: templatePermissions.hasTemplatePush }) })), _jsx(TabPanel, __assign({ value: TabValue.NETOPS }, { children: _jsx(ConfigTemplateTab, { type: "nom", canPushTemplates: templatePermissions.hasTemplatePush }) })), _jsx(TabPanel, __assign({ value: TabValue.SMF }, { children: _jsx(ConfigTemplateTab, { type: "smf", canPushTemplates: templatePermissions.hasTemplatePush }) })), _jsx(TabPanel, __assign({ value: TabValue.PORT }, { children: _jsx(ConfigTemplateTab, { type: "port_logging", canPushTemplates: templatePermissions.hasTemplatePush }) }))] })] })) })] })));
}
export default withAuthenticationRequired(ConfigTemplates, {
    rights: [
        'configure.configuration_templating.authentication_templates.view',
        'configure.configuration_templating.users_groups_templates.view',
        'configure.configuration_templating.script_templates.view',
        'configure.configuration_templating.smf_templates.view',
    ],
});
